import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSessionId, apiRequest } from '../lib/utils';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`/cart?session_id=${sessionId}`);
      setCart(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (menuItemId, quantity = 1, specialInstructions = '', removedIngredients = [], hasCombo = false, selectedSize = null, selectedAddons = []) => {
    try {
      await apiRequest(`/cart/add?session_id=${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({
          menu_item_id: menuItemId,
          quantity,
          special_instructions: specialInstructions,
          removed_ingredients: removedIngredients,
          has_combo: hasCombo,
          selected_size: selectedSize,
          selected_addons: selectedAddons
        })
      });
      await fetchCart();
      setIsOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const updateCartItem = async (menuItemId, quantity, specialInstructions = null) => {
    try {
      await apiRequest(`/cart/item/${menuItemId}?session_id=${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          quantity,
          special_instructions: specialInstructions
        })
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (menuItemId) => {
    try {
      await apiRequest(`/cart/item/${menuItemId}?session_id=${sessionId}`, {
        method: 'DELETE'
      });
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await apiRequest(`/cart?session_id=${sessionId}`, {
        method: 'DELETE'
      });
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  const cartTotal = cart.items.reduce((sum, item) => {
    let price = (item.selected_size === 'big' && item.price_big) ? item.price_big : item.price;
    let itemTotal = price * item.quantity;
    if (item.has_combo) itemTotal += 6.0 * item.quantity;
    const addonsTotal = (item.selected_addons || []).reduce((s, a) => s + (a.price || 0), 0);
    itemTotal += addonsTotal * item.quantity;
    return sum + itemTotal;
  }, 0);
  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      isOpen,
      setIsOpen,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      fetchCart,
      cartTotal,
      cartCount,
      sessionId
    }}>
      {children}
    </CartContext.Provider>
  );
};
